import type { JSONContent } from '@tiptap/react';

import type { LinkedContextItem } from '../../contracts/projects/linked-context';
import {
  formatRefTag,
  refTargetFrom,
  type RefTag,
} from '../../contracts/projects/ref-tags';
import { resolvePatientLabel } from './patientListName';
import { substituteStarterBundleItems } from './projects-starter-bundle-internal';

export const PROJECTS_SKILL_NODE_NAME = 'projectsSkillReference';
export const PROJECTS_STARTER_BUNDLE_NODE_NAME = 'projectsStarterBundle';
export const CHAT_OBJECT_TOKEN_NODE_NAME = 'chatObjectToken';

export const CHAT_OBJECT_KINDS = [
  'patient',
  'session',
  'library_document',
  'template',
  'task',
  'collection',
] as const;

export type ChatObjectKind = (typeof CHAT_OBJECT_KINDS)[number];

export interface ChatObjectTokenAttrs {
  readonly kind: ChatObjectKind;
  readonly id: string;
  readonly label: string;
  /**
   * Patient a `session` token belongs to, carried for the GLYPH only — the
   * token shows that patient's avatar instead of the generic recording mark.
   * Never rendered as text, so the label stays the session's own name.
   */
  readonly patientName?: string;
  readonly avatarSeed?: string;
  /**
   * Library-document sub-kind for a `library_document` token. Carried so the wire
   * preamble keeps the object's real kind (`@skill_file:` / `@upload:`) instead
   * of flattening every referenced document to `@work_output:`.
   */
  readonly documentKind?: 'artifact' | 'workflow_context' | 'input';
  /** True for a PDF form template; emits `@form_template:` on the wire. */
  readonly formTemplate?: boolean;
  /** The visit a `task` came from; the token opens that session's task list. */
  readonly sessionId?: string;
}

export interface ProjectsComposerSkillReference {
  readonly skillId: string;
  readonly recordingId?: string;
  readonly slug: string;
  readonly title: string;
  readonly kind?: 'skill' | 'workflow';
}

export interface ProjectsComposerTextSegment {
  readonly kind: 'text';
  readonly text: string;
}

export interface ProjectsComposerSkillSegment {
  readonly kind: 'skill';
  readonly skill: ProjectsComposerSkillReference;
}

export interface ProjectsComposerStarterBundleItem {
  readonly id: string;
  readonly label: string;
}

export interface ProjectsComposerStarterBundle {
  readonly starterId: string;
  readonly leadIn: string;
  readonly items: readonly ProjectsComposerStarterBundleItem[];
}

export interface ProjectsComposerStarterBundleSegment {
  readonly kind: 'starter_bundle';
  readonly bundle: ProjectsComposerStarterBundle;
}

export interface ProjectsComposerObjectSegment {
  readonly kind: 'object';
  readonly object: ChatObjectTokenAttrs;
}

export type ProjectsComposerPromptSegment =
  | ProjectsComposerTextSegment
  | ProjectsComposerSkillSegment
  | ProjectsComposerStarterBundleSegment
  | ProjectsComposerObjectSegment;

export interface ProjectsComposerPromptDraft {
  readonly text: string;
  readonly segments: readonly ProjectsComposerPromptSegment[];
}

export const EMPTY_PROJECTS_COMPOSER_PROMPT_DRAFT: ProjectsComposerPromptDraft = {
  text: '',
  segments: [],
};

/** Strip invisible chars used to re-trigger landing-composer prefills. */
export const stripComposerInvisibleChars = (text: string): string =>
  text.replace(/\u200B/g, '');

export const tiptapDocToProjectsComposerDraft = (
  doc: JSONContent | undefined
): ProjectsComposerPromptDraft => {
  if (!doc || !Array.isArray(doc.content)) return EMPTY_PROJECTS_COMPOSER_PROMPT_DRAFT;

  const segments: ProjectsComposerPromptSegment[] = [];

  doc.content.forEach((block, blockIndex) => {
    if (blockIndex > 0) segments.push({ kind: 'text', text: '\n' });
    walkNode(block, segments);
  });

  const merged = mergeAdjacentTextSegments(segments);

  return {
    text: projectsComposerDraftPlainText(merged),
    segments: merged,
  };
};

export const projectsComposerTextToTiptapDoc = (text: string): JSONContent => {
  if (text.length === 0) {
    return { type: 'doc', content: [{ type: 'paragraph' }] };
  }

  const lines = text.split('\n');
  return {
    type: 'doc',
    content: [
      {
        type: 'paragraph',
        content: lines.flatMap((line, index): JSONContent[] => {
          const nodes: JSONContent[] = [];
          if (index > 0) nodes.push({ type: 'hardBreak' });
          if (line.length > 0) nodes.push({ type: 'text', text: line });
          return nodes;
        }),
      },
    ],
  };
};

export const projectsComposerDraftToTiptapDoc = (
  draft: ProjectsComposerPromptDraft
): JSONContent => {
  if (draft.segments.length === 0) {
    return projectsComposerTextToTiptapDoc(draft.text);
  }

  const content = draft.segments.flatMap((segment): JSONContent[] => {
    if (segment.kind === 'skill') {
      return [
        {
          type: PROJECTS_SKILL_NODE_NAME,
          attrs: buildProjectsSkillNodeAttrs(segment.skill),
        },
      ];
    }

    // The composer editor registers `ChatObjectToken`; TipTap swaps an
    // unregistered node for an EMPTY doc, so only that editor's nodes go here.
    if (segment.kind === 'object') {
      return [{ type: CHAT_OBJECT_TOKEN_NODE_NAME, attrs: segment.object }];
    }

    if (segment.kind === 'starter_bundle') {
      return [
        {
          type: PROJECTS_STARTER_BUNDLE_NODE_NAME,
          attrs: {
            starterId: segment.bundle.starterId,
            leadIn: segment.bundle.leadIn,
            items: segment.bundle.items,
          },
        },
      ];
    }

    return textSegmentToTiptapContent(segment.text);
  });

  return {
    type: 'doc',
    content: [
      {
        type: 'paragraph',
        content: content.length > 0 ? content : undefined,
      },
    ],
  };
};

const buildProjectsSkillNodeAttrs = (
  skill: ProjectsComposerSkillReference
): Record<string, unknown> => ({
  skillId: skill.skillId,
  ...(skill.recordingId ? { recordingId: skill.recordingId } : {}),
  slug: skill.slug,
  title: skill.title,
  ...(skill.kind ? { kind: skill.kind } : {}),
});

const walkNode = (node: JSONContent, segments: ProjectsComposerPromptSegment[]): void => {
  if (node.type === 'text' && typeof node.text === 'string') {
    segments.push({ kind: 'text', text: node.text });
    return;
  }

  if (node.type === 'hardBreak') {
    segments.push({ kind: 'text', text: '\n' });
    return;
  }

  if (node.type === CHAT_OBJECT_TOKEN_NODE_NAME) {
    const object = chatObjectAttrsFromRecord(node.attrs);
    if (!object) return;
    segments.push({ kind: 'object', object });
    return;
  }

  if (node.type === PROJECTS_SKILL_NODE_NAME) {
    const attrs = (node.attrs ?? {}) as Record<string, unknown>;
    const skillId =
      typeof attrs.skillId === 'string'
        ? attrs.skillId
        : typeof attrs.id === 'string'
          ? attrs.id
          : '';
    const recordingId =
      typeof attrs.recordingId === 'string' && attrs.recordingId.length > 0
        ? attrs.recordingId
        : undefined;
    const slug = typeof attrs.slug === 'string' ? attrs.slug : '';
    const title = typeof attrs.title === 'string' ? attrs.title : slug;
    const skillKind =
      attrs.kind === 'skill' || attrs.kind === 'workflow' ? attrs.kind : undefined;
    if (skillId.length > 0 || slug.length > 0) {
      segments.push({
        kind: 'skill',
        skill: {
          skillId,
          ...(recordingId ? { recordingId } : {}),
          slug,
          title,
          ...(skillKind ? { kind: skillKind } : {}),
        },
      });
    }
    return;
  }

  if (node.type === PROJECTS_STARTER_BUNDLE_NODE_NAME) {
    const attrs = (node.attrs ?? {}) as Record<string, unknown>;
    const starterId = typeof attrs.starterId === 'string' ? attrs.starterId : '';
    const leadIn = typeof attrs.leadIn === 'string' ? attrs.leadIn : '';
    const items = normalizeStarterBundleItems(attrs.items);
    if (starterId && leadIn) {
      segments.push({
        kind: 'starter_bundle',
        bundle: { starterId, leadIn, items },
      });
    }
    return;
  }

  if (Array.isArray(node.content)) {
    node.content.forEach((child) => walkNode(child, segments));
  }
};

const mergeAdjacentTextSegments = (
  segments: readonly ProjectsComposerPromptSegment[]
): ProjectsComposerPromptSegment[] => {
  const merged: ProjectsComposerPromptSegment[] = [];

  for (const segment of segments) {
    const previous = merged.at(-1);
    if (segment.kind === 'text' && previous?.kind === 'text') {
      merged[merged.length - 1] = {
        kind: 'text',
        text: previous.text + segment.text,
      };
      continue;
    }
    merged.push(segment);
  }

  return merged;
};

const textSegmentToTiptapContent = (text: string): JSONContent[] => {
  if (text.length === 0) return [];

  return text.split('\n').flatMap((line, index): JSONContent[] => {
    const nodes: JSONContent[] = [];
    if (index > 0) nodes.push({ type: 'hardBreak' });
    if (line.length > 0) nodes.push({ type: 'text', text: line });
    return nodes;
  });
};

export const hasProjectsComposerSkill = (draft: ProjectsComposerPromptDraft): boolean =>
  draft.segments.some((segment) => segment.kind === 'skill');

/** Drop skill chips while preserving typed text and Work-home starter bundles. */
export const stripProjectsComposerSkills = (
  draft: ProjectsComposerPromptDraft
): ProjectsComposerPromptDraft => {
  if (!hasProjectsComposerSkill(draft)) return draft;
  return draftWithSegments(draft.segments.filter((segment) => segment.kind !== 'skill'));
};

/**
 * Where object tokens are not offered (Evidence, a locked in-session chat),
 * each token becomes its plain name as text, in place. Returns `draft` itself
 * when there is nothing to convert. The token node stays registered in every
 * mode, so a doc holding one still loads; only inserting tokens is gated.
 */
export const normalizeDraftForMode = (
  draft: ProjectsComposerPromptDraft,
  objectTokensEnabled: boolean
): ProjectsComposerPromptDraft => {
  if (
    objectTokensEnabled ||
    !draft.segments.some((segment) => segment.kind === 'object')
  ) {
    return draft;
  }
  return draftWithSegments(
    draft.segments.map((segment) =>
      segment.kind === 'object'
        ? { kind: 'text', text: chatObjectPlainLabel(segment.object) }
        : segment
    )
  );
};

export const getProjectsComposerStarterBundle = (
  draft: ProjectsComposerPromptDraft
): ProjectsComposerStarterBundle | undefined => {
  const segment = draft.segments.find((item) => item.kind === 'starter_bundle');
  return segment?.kind === 'starter_bundle' ? segment.bundle : undefined;
};

export const normalizeStarterBundleItems = (
  raw: unknown
): ProjectsComposerStarterBundleItem[] => {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const candidate = item as Record<string, unknown>;
    return typeof candidate.id === 'string' && typeof candidate.label === 'string'
      ? [{ id: candidate.id, label: candidate.label }]
      : [];
  });
};

const starterBundleItems = (
  items: ProjectsComposerStarterBundle['items']
): readonly ProjectsComposerStarterBundleItem[] => normalizeStarterBundleItems(items);

export const withProjectsComposerStarterBundle = (
  draft: ProjectsComposerPromptDraft,
  bundle: ProjectsComposerStarterBundle
): ProjectsComposerPromptDraft => {
  const withoutBundle = stripManagedBundleSeparator(
    draft.segments,
    draft.segments.some((segment) => segment.kind === 'starter_bundle')
  );
  const unrelated = withoutBundle.filter((segment) => segment.kind !== 'starter_bundle');
  const separator = unrelated.length > 0 ? [{ kind: 'text' as const, text: ' ' }] : [];
  const segments: ProjectsComposerPromptSegment[] = [
    {
      kind: 'starter_bundle',
      bundle: {
        starterId: bundle.starterId,
        leadIn: bundle.leadIn,
        items: starterBundleItems(bundle.items),
      },
    },
    ...separator,
    ...unrelated,
  ];
  return draftWithSegments(segments);
};

export const withoutProjectsComposerStarterBundle = (
  draft: ProjectsComposerPromptDraft,
  starterId?: string
): ProjectsComposerPromptDraft => {
  const shouldRemove = draft.segments.some(
    (segment) =>
      segment.kind === 'starter_bundle' &&
      (starterId === undefined || segment.bundle.starterId === starterId)
  );
  const segments = stripManagedBundleSeparator(draft.segments, shouldRemove).filter(
    (segment) =>
      segment.kind !== 'starter_bundle' ||
      (starterId !== undefined && segment.bundle.starterId !== starterId)
  );
  return draftWithSegments(segments);
};

export const projectsComposerStarterBundleText = (
  bundle: ProjectsComposerStarterBundle
): string => {
  const labels = normalizeStarterBundleItems(bundle.items)
    .map((item) => item.label)
    .join(', ');
  return substituteStarterBundleItems(bundle.leadIn, labels);
};

export const parseProjectsStarterBundleItems = (
  raw: string | undefined
): ProjectsComposerStarterBundleItem[] => {
  try {
    return normalizeStarterBundleItems(JSON.parse(raw ?? '[]'));
  } catch {
    return [];
  }
};

export const renderProjectsStarterBundleHtml = (
  attrs: Partial<ProjectsComposerStarterBundle>
): [string, Record<string, string>, string] => {
  const starterId = attrs.starterId ?? '';
  const leadIn = attrs.leadIn ?? '';
  const items = normalizeStarterBundleItems(attrs.items);
  return [
    'span',
    {
      'data-projects-starter-bundle': '',
      'data-testid': 'projects-composer-starter-bundle',
      'data-starter-id': starterId,
      'data-lead-in': leadIn,
      'data-items': JSON.stringify(items),
      class: 'whitespace-normal break-words',
    },
    projectsComposerStarterBundleText({ starterId, leadIn, items }),
  ];
};

/**
 * Returns the first workflow-kind skill reference in the draft, or undefined if
 * the draft only contains recording-based skills (or no skills at all).
 */
export const getProjectsComposerWorkflow = (
  draft: ProjectsComposerPromptDraft
): ProjectsComposerSkillReference | undefined => {
  for (const seg of draft.segments) {
    if (seg.kind === 'skill' && seg.skill.kind === 'workflow') return seg.skill;
  }
  return undefined;
};

export const isProjectsComposerDraftEmpty = (
  draft: ProjectsComposerPromptDraft
): boolean => draft.text.trim().length === 0 && !hasProjectsComposerSkill(draft);

export const chatObjectPlainLabel = (object: ChatObjectTokenAttrs): string =>
  object.kind === 'patient'
    ? (resolvePatientLabel(null, object.label) ?? '')
    : object.label.trim();

export const composerTokenToRef = (object: ChatObjectTokenAttrs): RefTag | null => {
  const target = refTargetFrom(
    object.kind === 'library_document' ? 'file' : object.kind,
    object.id
  );
  const labelHint = chatObjectPlainLabel(object);
  return target && labelHint ? { ...target, labelHint } : null;
};

export function chatObjectTokenAttrsForLinkedItem(
  item: LinkedContextItem
): ChatObjectTokenAttrs {
  if (item.type === 'session') {
    const patientName = resolvePatientLabel(null, item.patientName);
    return {
      kind: 'session',
      id: item.sessionId,
      label: item.tokenLabel?.trim() || item.sessionName,
      ...(patientName ? { patientName } : {}),
      ...(patientName && item.avatarSeed ? { avatarSeed: item.avatarSeed } : {}),
    };
  }
  if (item.type === 'library_document') {
    return {
      kind: 'library_document',
      id: item.documentId,
      label: item.filename,
      documentKind: item.documentKind,
    };
  }
  if (item.type === 'template') {
    return {
      kind: 'template',
      id: item.templateId,
      label: item.label,
      ...(item.formTemplate ? { formTemplate: true } : {}),
    };
  }
  if (item.type === 'task') {
    return {
      kind: 'task',
      id: item.taskId,
      label: item.label,
      ...(item.sessionId ? { sessionId: item.sessionId } : {}),
    };
  }
  if (item.type === 'collection') {
    return { kind: 'collection', id: item.collectionId, label: item.label };
  }
  return {
    kind: 'patient',
    // NEVER fall back to the name. This id is read as ``patient_profile_id`` on
    // the wire, and the raw name still carries the demographics the label has
    // just stripped ("Ada Lovelace, F, 210"). A profile-less patient has no id,
    // and `serializeLinkedContext` already emits a bare `@patient: Name` then.
    id: item.patientId ?? '',
    label: resolvePatientLabel(null, item.patientName) ?? '',
    ...(item.avatarSeed ? { avatarSeed: item.avatarSeed } : {}),
  };
}

/**
 * Inverse of `chatObjectTokenAttrsForLinkedItem`: the linked-context item an
 * inline `@` token contributes to the wire preamble.
 */
export function linkedContextItemForChatObject(
  object: ChatObjectTokenAttrs
): LinkedContextItem {
  const { kind, id, label, patientName, documentKind, formTemplate, sessionId } = object;
  switch (kind) {
    case 'patient':
      // Only a resolved `patient_profile_id` becomes an id; never the name.
      return { type: 'patient', patientName: label, ...(id ? { patientId: id } : {}) };
    case 'session':
      return {
        type: 'session',
        sessionId: id,
        sessionName: label,
        ...(patientName ? { patientName } : {}),
      };
    case 'library_document':
      return {
        type: 'library_document',
        documentId: id,
        filename: label,
        documentKind: documentKind ?? 'artifact',
      };
    case 'template':
      return {
        type: 'template',
        templateId: id,
        label,
        ...(formTemplate ? { formTemplate: true } : {}),
      };
    case 'task':
      return { type: 'task', taskId: id, label, ...(sessionId ? { sessionId } : {}) };
    case 'collection':
      return { type: 'collection', collectionId: id, label };
  }
}

export const chatObjectWireText = (object: ChatObjectTokenAttrs): string => {
  const ref = composerTokenToRef(object);
  return ref ? formatRefTag(ref) : chatObjectPlainLabel(object);
};

const omitSkill = (): string => '';

const skillSlashText = (skill: ProjectsComposerSkillReference): string => {
  const slug = skill.slug.trim();
  return slug.length > 0 ? `/${slug}` : skill.title.trim();
};

const segmentText = (
  segment: ProjectsComposerPromptSegment,
  objectText: (object: ChatObjectTokenAttrs) => string,
  skillText: (skill: ProjectsComposerSkillReference) => string
): string => {
  if (segment.kind === 'text') return segment.text;
  if (segment.kind === 'starter_bundle') {
    return projectsComposerStarterBundleText(segment.bundle);
  }
  if (segment.kind === 'object') return objectText(segment.object);
  return skillText(segment.skill);
};

/**
 * The ONE owner of how segment texts join, shared by the plain, wire and
 * display forms so they cannot disagree. A starter bundle is a whole sentence,
 * so it gets a single space against a neighbour that does not already bring
 * whitespace to the seam ("…consult note" + "Then…" must not fuse).
 */
const joinSegments = (
  segments: readonly ProjectsComposerPromptSegment[],
  objectText: (object: ChatObjectTokenAttrs) => string,
  skillText: (skill: ProjectsComposerSkillReference) => string
): string => {
  let joined = '';
  let previous: ProjectsComposerPromptSegment | undefined;
  for (const segment of segments) {
    const text = segmentText(segment, objectText, skillText);
    if (text.length === 0) continue;
    const bundleSeam =
      previous !== undefined &&
      (previous.kind === 'starter_bundle' || segment.kind === 'starter_bundle');
    if (bundleSeam && !/\s$/.test(joined) && !/^\s/.test(text)) {
      joined += ' ';
    }
    joined += text;
    previous = segment;
  }
  return joined;
};

export const projectsComposerDraftPlainText = (
  segments: readonly ProjectsComposerPromptSegment[]
): string => joinSegments(segments, chatObjectPlainLabel, omitSkill);

export const projectsComposerDraftWireText = (
  segments: readonly ProjectsComposerPromptSegment[]
): string => joinSegments(segments, chatObjectWireText, omitSkill);

export const projectsComposerDraftDisplayText = (
  draft: ProjectsComposerPromptDraft
): string | undefined => {
  const raw =
    draft.segments.length > 0
      ? joinSegments(draft.segments, chatObjectWireText, skillSlashText)
      : draft.text;
  const text = stripComposerInvisibleChars(raw).trim();
  return text.length > 0 ? text : undefined;
};

export const isChatObjectKind = (value: unknown): value is ChatObjectKind =>
  typeof value === 'string' && (CHAT_OBJECT_KINDS as readonly string[]).includes(value);

export const chatObjectAttrsFromRecord = (raw: unknown): ChatObjectTokenAttrs | null => {
  if (!raw || typeof raw !== 'object') return null;
  const attrs = raw as Record<string, unknown>;
  if (!isChatObjectKind(attrs.kind)) return null;
  const id = typeof attrs.id === 'string' ? attrs.id : '';
  const label = typeof attrs.label === 'string' ? attrs.label : '';
  if (id.length === 0 && label.length === 0) return null;
  const patientName =
    typeof attrs.patientName === 'string' && attrs.patientName.trim().length > 0
      ? attrs.patientName
      : undefined;
  const avatarSeed =
    typeof attrs.avatarSeed === 'string' && attrs.avatarSeed.trim().length > 0
      ? attrs.avatarSeed
      : undefined;
  const sessionId =
    typeof attrs.sessionId === 'string' && attrs.sessionId.length > 0
      ? attrs.sessionId
      : undefined;
  const documentKind =
    attrs.documentKind === 'artifact' ||
    attrs.documentKind === 'workflow_context' ||
    attrs.documentKind === 'input'
      ? attrs.documentKind
      : undefined;
  return {
    kind: attrs.kind,
    id,
    label,
    ...(patientName ? { patientName } : {}),
    ...(avatarSeed ? { avatarSeed } : {}),
    ...(documentKind ? { documentKind } : {}),
    ...(attrs.formTemplate === true || attrs.formTemplate === 'true'
      ? { formTemplate: true }
      : {}),
    ...(sessionId ? { sessionId } : {}),
  };
};

const draftWithSegments = (
  segments: readonly ProjectsComposerPromptSegment[]
): ProjectsComposerPromptDraft => {
  const merged = mergeAdjacentTextSegments(segments).filter(
    (segment) => segment.kind !== 'text' || segment.text.length > 0
  );
  return {
    segments: merged,
    text: projectsComposerDraftPlainText(merged),
  };
};

const stripManagedBundleSeparator = (
  segments: readonly ProjectsComposerPromptSegment[],
  shouldStrip: boolean
): ProjectsComposerPromptSegment[] => {
  const next = [...segments];
  if (!shouldStrip || next[0]?.kind !== 'starter_bundle') return next;
  const following = next[1];
  if (following?.kind !== 'text' || !following.text.startsWith(' ')) return next;
  next[1] = { kind: 'text', text: following.text.slice(1) };
  return next;
};
